// v2 엔드포인트 SSOT. 모든 경로는 컨트롤러 `@RequestMapping` + `@*Mapping` 실측 기준.
// 도메인별 requests/ 는 점진 합류 — 경로는 미리 등록해도 무해.
// 경로는 base 변수 없이 `/api/v2` 리터럴 반복 — 검색·정합 확인이 직관적(변수 우회 제거).
//
// 엔드포인트는 경로와 함께 **자기 transport 정책**(`requiresAuth`·`retryAfterTokenRefresh`)을 선언한다
// (app-legacy 와 동일 패턴). 정책은 요청 config 에 실려 인터셉터까지 전달된다(`http.ts`) — 인터셉터가
// URL 목록을 매칭하지 않으므로 동적 경로도 정적 경로와 똑같이 동작한다.
//
// 본 패키지는 **데이터 도메인 전용**(auth 는 v3=app-legacy 소유)이라 현재 전 엔드포인트가 인증 필요 +
// 401 재시도 대상이다. 팩토리·기본값 없이 항목마다 직접 명시 — 한 항목만 읽어도 동작이 다 보이게.

/** 엔드포인트 = 경로 + transport 정책. `http.*` 가 이 객체를 그대로 받는다. */
export interface Endpoint {
  path: string;
  /**
   * `Authorization: Bearer <access>` 자동 부여 여부.
   * false = 인터셉터가 헤더에 손대지 않는다(무인증 또는 호출처가 헤더 소유).
   */
  requiresAuth: boolean;
  /**
   * 401 을 받았을 때 access 재발급 후 **1회 재시도**할지 여부.
   * false = 이 엔드포인트의 401 은 "세션 만료"가 아니라 "입력 틀림 / 비즈니스 거부".
   */
  retryAfterTokenRefresh: boolean;
}

export const endpoints = {
  subscription: {
    /** GET — 구독 목록 조회 (SubscriptionRestController) */
    search: {
      path: '/api/v2/subscriptions',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** POST — 발전소 구독 신청 (PlantSubscriptionController) */
    applyByPlant: (plantId: number): Endpoint => ({
      path: `/api/v2/plants/${plantId}/subscriptions`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
    /**
     * GET — 발전소 구독 내역(사이클) 목록 (PlantSubscriptionController). `applyByPlant` 와 **동일 path,
     * 다른 method** — 정책·타입이 갈리므로 별도 항목으로 둔다(호출부가 의도를 이름으로 드러냄).
     */
    cyclesByPlant: (plantId: number): Endpoint => ({
      path: `/api/v2/plants/${plantId}/subscriptions`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
    /**
     * GET — 발전소 **현재 구독 단건** (PlantSubscriptionController). 스웨거 명시 용도:
     * "무통장 입금 주문 전 결제 금액·상품 유형·과금용 RTU-인버터 정보를 확인" — 수동 결제 등록 시트의 원천.
     */
    currentByPlant: (plantId: number): Endpoint => ({
      path: `/api/v2/plants/${plantId}/subscriptions/current`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
    /** PATCH — 구독 마스터 약정 기간·메모 수정 (PlantSubscriptionController) */
    updateByPlant: (plantId: number): Endpoint => ({
      path: `/api/v2/plants/${plantId}/subscriptions`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
    /** DELETE — 구독 해지 (종료일은 유형별 정책으로 산정 후 CANCELED 전이) */
    cancelByPlant: (plantId: number): Endpoint => ({
      path: `/api/v2/plants/${plantId}/subscriptions/cancel`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
    /** PUT — 구독 ticket 약정 기간 수정. ⚠️ 현 스웨거에 없음(폐기 여부 BE 확인 대기) */
    updateTicket: (plantId: number, ticketId: number): Endpoint => ({
      path: `/api/v2/plants/${plantId}/tickets/${ticketId}`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
    /** PUT — 발전소 구독 상태 변경. ⚠️ 현 스웨거에 없음(폐기 여부 BE 확인 대기) */
    updateStatusByPlant: (plantId: number): Endpoint => ({
      path: `/api/v2/plants/${plantId}/subscriptions/status`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
  },

  common: {
    // 코드 조회 (CommonCodeController). 전부 파라미터 없는 GET. code↔name 라벨 매핑.
    euser: {
      path: '/api/v2/common/euser',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    bank: {
      path: '/api/v2/common/bank',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    subs: {
      path: '/api/v2/common/subs',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    subsStatus: {
      path: '/api/v2/common/subs/status',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** GET — 다중 부모 공통 코드 조회(`?parentCodes=EUSER,BANK,...`). 부모별 code↔name 목록 일괄 조회. */
    codes: {
      path: '/api/v2/common/codes',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
  },

  plant: {
    /** GET — 발전소 목록 (PlantRestController.getPlants) */
    list: {
      path: '/api/v2/plants',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** GET — 발전소 단건 (PlantRestController.getPlant) */
    byId: (plantId: number): Endpoint => ({
      path: `/api/v2/plants/${plantId}`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
    /**
     * PATCH — 발전소 정보 수정 (백오피스, 수정 가능 필드만). [최신 swagger 기준 — BE 미구현/미검증]
     * ⚠️ 경로가 단수 `plant`(조회는 복수 `plants`) — swagger 표기 그대로. [TODO:be-verify] 단·복수 정합.
     */
    update: (plantId: number): Endpoint => ({
      path: `/api/v2/plant/${plantId}`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
    /** GET — 발전소 연동 계정 목록(활성 계정, accountId 오름차순, 서버 페이지네이션) */
    accounts: (plantId: number): Endpoint => ({
      path: `/api/v2/plants/${plantId}/accounts`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
  },

  account: {
    base: {
      path: '/api/v2/accounts',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    // accountId 는 BE 가 String 직렬화(profile·account 노드 모두 string) — getById 는 string 으로 호출.
    // update 등 number 호출처도 있어 string|number 수용(템플릿 보간엔 무해). [§5-7]
    byId: (accountId: string | number): Endpoint => ({
      path: `/api/v2/accounts/${accountId}`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
    check: {
      path: '/api/v2/accounts/check',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    passwordReset: (accountId: number): Endpoint => ({
      path: `/api/v2/accounts/${accountId}/password-reset`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
    plants: (accountId: number): Endpoint => ({
      path: `/api/v2/accounts/${accountId}/plants`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
    plantById: (accountId: number, plantId: number): Endpoint => ({
      path: `/api/v2/accounts/${accountId}/plants/${plantId}`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
    /**
     * POST — 계정-발전소 역할 설정(OWNR/SAFE/INST + 대표 여부). SYSTEM_ADMIN 전용.
     * ⚠️ 경로 단수 `plant`(목록은 복수 `plants`) — swagger 표기 그대로. [TODO:be-verify] 단·복수 정합.
     */
    plantRoles: (accountId: number, plantId: number): Endpoint => ({
      path: `/api/v2/accounts/${accountId}/plant/${plantId}/roles`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
    /**
     * DELETE — 계정-발전소 역할 mapping 삭제(연동 해제). 해당 발전소에 사용 중 구독·READY ticket 있으면 삭제 불가.
     * SYSTEM_ADMIN 전용. ⚠️ 경로 `plant-roles`(설정 POST 는 `plant/{id}/roles`) — swagger 표기 그대로.
     */
    deletePlantRole: (accountId: number, plantId: number): Endpoint => ({
      path: `/api/v2/accounts/${accountId}/plant-roles/${plantId}`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
  },

  agreement: {
    byType: (agreementType: string): Endpoint => ({
      path: `/api/v2/agreements/${agreementType}`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
    terms: {
      path: '/api/v2/agreements/terms',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    privacy: {
      path: '/api/v2/agreements/privacy',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    refund: {
      path: '/api/v2/agreements/refund',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
  },

  product: {
    base: {
      path: '/api/v2/products',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** 발전소 구독(PLANT_SUB) 상품 목록 — `plantId` 로 BE 가 자격·요금 산정. 페이지네이션 없음(전량). */
    plantSub: {
      path: '/api/v2/products/plant-sub',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    } as Endpoint,
    byId: (productId: number): Endpoint => ({
      path: `/api/v2/products/${productId}`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
    activate: (productId: number): Endpoint => ({
      path: `/api/v2/products/${productId}/activate`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
  },

  order: {
    /** billingId 는 외부 공개용 문자열 ID (예: "gHoveVr3r0y7") */
    billing: (billingId: string): Endpoint => ({
      path: `/api/v2/orders/billing/${billingId}`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
    manual: {
      path: '/api/v2/orders/manual',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** PUT — 구독중 정기결제 주문의 결제수단(빌링키) 변경 (OrderController.changeBilling) */
    changeBilling: (orderId: number): Endpoint => ({
      path: `/api/v2/orders/${orderId}/billing`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
  },

  billing: {
    base: {
      path: '/api/v2/billings',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    /** billingId 는 외부 공개용 문자열 ID (예: "gHoveVr3r0y7") */
    byId: (billingId: string): Endpoint => ({
      path: `/api/v2/billings/${billingId}`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
  },

  receipt: {
    byPlant: (plantId: number): Endpoint => ({
      path: `/api/v2/plants/${plantId}/receipts`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
    byId: (receiptId: number): Endpoint => ({
      path: `/api/v2/receipts/${receiptId}`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
    manual: (receiptId: number): Endpoint => ({
      path: `/api/v2/receipts/${receiptId}/manual`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
    /**
     * DELETE — 영수증(주문) 취소. `byId` 와 **동일 path, 다른 method** 라 의도를 이름으로 드러내려 별도 항목.
     * 전액 취소만 지원(잔액과 다르면 400) · 빌링(토스)·CMS 결제는 현재 BE 가 거부.
     */
    cancel: (receiptId: number): Endpoint => ({
      path: `/api/v2/receipts/${receiptId}`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
  },
} as const;
